import React, { useRef, useEffect, useState, FocusEvent, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, InputGroup, Button } from "react-bootstrap";
import { ActionHandlerFunction, SearchResultItem, MailSearchMatch, UserProfile, Thread, ThreadsPage } from "@preveil-api";
import {
  useAppSelector, useAppDispatch, useSearchMutation, SearchFilters, SearchApiResponse, MailResultItem, Contacts, MailErrorMessages, MailUIActionTypes, MessageAnchors,
  QueryParamKeys, MessageHandlerDisplayType, Message, PrivateRoutes, SearchResultFilter, isSearchMailbox, DefaultMailboxes, MailThread, SearchMatchesType
} from "src/common";
import { Icon } from "src/components";
import SearchAutocomplete from "./SearchAutocomplete.component";
import { uiActions, mailActions } from "src/store";
import _ from "lodash";

type AllProps = {
  searchMode: boolean;
  handleSearchModeAction: (search_mode: boolean) => void;
  handleAction: ActionHandlerFunction;
}

function SearchFormComponent(props: AllProps) {
  const { searchMode, handleSearchModeAction, handleAction } = props;
  const { mailbox } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user_id = useAppSelector((state) => state.account.current_account?.user_id);
  const search_term_selector = useAppSelector((state) => state.mail.search_term);
  const [mail_results, setMailResults] = useState<MailSearchMatch[]>();
  const [user_results, setUserResults] = useState<UserProfile[]>();
  const [search_term, setSearchTerm] = useState<string | null>(null);
  const search_ref = useRef<HTMLDivElement>(null);
  const [searchMail, { isLoading }] = useSearchMutation();

  // Description: Update search form state when query is coming from page load QS string
  useEffect(() => {
    (!!search_term_selector && !search_term) && setSearchTerm(search_term_selector);
  }, [search_term_selector]);

  useEffect(() => {
    (isSearchMailbox(mailbox || "") && !!search_term && !!user_id) &&
      onSearchHandler(search_term, user_id, true);
  }, [mailbox]);

  // Description: Callback with debounce to handle user input in textbox
  const onSearchHandler = useCallback(
    _.debounce((_search_term: string, _user_id: string, is_search: boolean) => {
      // NOTE: Handle contacts search
      if (!!user_id) {
        const user_matches = new Contacts(user_id).search(_search_term);
        setUserResults(user_matches);
      };

      // NOTE: Handle Mail search
      searchMail({
        userId: _user_id,
        query: [{
          type: SearchFilters.FullTextSearchFilter,
          value: _search_term
        }]
      }).unwrap()
        .then((response: SearchApiResponse) => {
          const filter_matches: SearchMatchesType = { to: [], from: [], message: [] };
          const mail_matches = _.map(response, (mail_match: SearchResultItem) => {
            const result_item = new MailResultItem(mail_match, _search_term);
            // NOTE: Update threads and filters if this is search result view only
            if (is_search) {
              const thread_id = mail_match.thread.thread_id;
              const filter_types = result_item.filter_types;
              (filter_types.includes(SearchResultFilter.to) && !filter_matches.to.includes(thread_id)) && filter_matches.to.push(thread_id);
              (filter_types.includes(SearchResultFilter.from) && !filter_matches.from.includes(thread_id)) && filter_matches.from.push(thread_id);
              (filter_types.includes(SearchResultFilter.message) && !filter_matches.message.includes(thread_id)) && filter_matches.message.push(thread_id);
            }
            return result_item.best_match;
          });
          is_search && setThreadsSuccess(response, filter_matches);
          !!mail_matches && setMailResults(mail_matches);
          dispatch(mailActions.setSearchTerm(_search_term));
        })
        .catch((error: unknown) => {
          const msg = new Message(MailErrorMessages.error_searching.replace(MessageAnchors.search_term, _search_term), MessageHandlerDisplayType.toastr);
          dispatch(uiActions.handleRequestErrors(msg, error));
        });
    }, 300), []);

  useEffect(() => {
    (!!search_term && search_term.length > 0 && !!user_id) &&
      onSearchHandler(search_term, user_id, isSearchMailbox(mailbox || ""));
  }, [search_term]);

  // Description: Handle the Hide and show of the mobile search box
  useEffect(() => {
    function handleClickOutside(event: any) {
      (search_ref.current && !search_ref.current.contains(event.target)) && handleSearchModeAction(false);
    }
    // Description: Bind the outside event listener for closing the search
    document.addEventListener("mousedown", handleClickOutside);
    return () => { // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [search_ref]);

  // Description: Load results into store for list display
  // NOTE: Only applies for Search Result View so also add filters data
  function setThreadsSuccess(response: SearchApiResponse, matches?: SearchMatchesType) {
    const threads = _.map(response, (mail_match: SearchResultItem) => {
      // NOTE: Stamp the best-ranked match's real mailbox_id onto the search thread so the results-row click resolves the
      // home folder by id (same choice as the autocomplete dropdown), not by the unreliable thread-level leaf (DA-4685).
      const best_match = (!!mail_match.email_matches && mail_match.email_matches.length > 0) ?
        mail_match.email_matches.reduce((em_a, em_b) => em_a.rank >= em_b.rank ? em_a : em_b) : undefined;
      return { ...new MailThread(mail_match.thread).thread, mailbox_id: best_match?.email.mailbox_id };
    });
    handleSetServiceData(threads, matches);
  }

  // Description: Handles set threads and reset, sets pagination
  function handleSetServiceData(threads: Thread[], matches?: SearchMatchesType) {
    const thread_page: ThreadsPage = {
      mailbox_id: DefaultMailboxes.search,
      threads: !!threads ? threads : [],
      total: threads.length
    };
    dispatch(mailActions.setThreadsSuccess(thread_page));
    dispatch(mailActions.setSearchMatches(matches));
    handleAction({ actionType: MailUIActionTypes.SetThreadsSuccess, params: thread_page });
  }

  // Description: Submit Search Mail
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    e.stopPropagation();
    handleSearchModeAction(false);
    const qs = !!search_term ? `?${QueryParamKeys.SEARCH_RESULT_QUERY_KEY}=${encodeURIComponent(search_term)}` : "";
    navigate(`/${PrivateRoutes.mail_search_route}${qs}`);
  };

  // Description: Submit Search Mail  
  function handleReset() {
    setSearchTerm(null);
    handleSearchModeAction(false);
    isSearchMailbox(mailbox || "") && handleSetServiceData([]);
    dispatch(mailActions.setSearchTerm());
    dispatch(mailActions.setSearchFilter(SearchResultFilter.all));
  }

  // Description: Set the current state for controlled form and propagate to store state
  function handleChange(e: FocusEvent<HTMLInputElement>) {
    setSearchTerm(e.currentTarget.value);
    !searchMode && handleSearchModeAction(true);
  }

  // Description: handle autocomplete set match
  function handleAutoCompleteAction(search_mode: boolean, _search_term?: string) {
    !!_search_term && setSearchTerm(_search_term);
    handleSearchModeAction(search_mode);
  }

  return <div className="search-box">
    <div className="search-form" ref={search_ref} >
      <Form noValidate onSubmit={handleSubmit}>
        <Form.Label htmlFor="search_mail_tb" visuallyHidden>Search Mail</Form.Label>
        <InputGroup>
          <Form.Control
            id="search_mail_tb"
            name="search_mail"
            autoComplete="off"
            placeholder="Search Mail"
            aria-label="Search Mail"
            value={search_term || ""}
            onChange={handleChange}
            onFocus={(_e: FocusEvent<HTMLInputElement>) => { !searchMode && handleSearchModeAction(true); }} />
          {
            !!search_term &&
            <Button variant="icon" aria-label="Clear Search"
              onClick={() => handleReset()}><Icon className="ficon-x" /></Button>
          }
          <Button variant="icon" type="submit" className={(!search_term) ? "text-muted-light" : ""} aria-label="Search Mail"><Icon className="ficon-search" /></Button>
        </InputGroup>
      </Form>{
        (!!search_term && !!user_id && searchMode) &&
        <SearchAutocomplete
          handleAction={handleAutoCompleteAction}
          searchTerm={search_term}
          mailResults={mail_results}
          userResults={user_results}
          isLoading={isLoading}
        />
      }
    </div>
    {/* Mobile Version */}
    <Button variant="icon"
      onClick={() => handleSearchModeAction(true)}
      className="toggle-search">
      <Icon className="ficon-search" />
    </Button>
  </div>;
}

export default React.memo(SearchFormComponent);
